import { useEffect, useState } from "react";
import { Sidebar } from "../components/layout/Sidebar";
import { Topbar } from "../components/layout/Topbar";
import { SetupWizard } from "./setup/SetupWizard";
import { ExecutiveDashboardView } from "./ExecutiveDashboardView";
import { OpportunityInsightsView } from "./OpportunityInsightsView";
import { SpendIntelligenceView } from "./SpendIntelligenceView";
import { SupplierMarketView } from "./SupplierMarketView";
import CostModelAnalysisView from "./CostModelAnalysisView";
import CostModelAnalysisStandalone from "./CostModelAnalysisStandalone";
import OnDemandCostAnalysisView from "./OnDemandCostAnalysisView";
import { CostModelWorkflowView } from "./CostModelWorkflowView";
import { SupplierRiskAnalysisView } from "./SupplierRiskAnalysisView";
import { POMismatchResolutionView } from "./POMismatchResolutionView";
import { IacEligibilityEngineView } from "./IacEligibilityEngineView";
import { PrCreationView } from "./PrCreation/PrCreationView";
import { DashboardEmptyView } from "./emptyStates/DashboardEmptyView";
import { ProjectsEmptyView } from "./emptyStates/ProjectsEmptyView";
import { ProjectsListView } from "./ProjectsListView";
import { useExcelData } from "../../context/ExcelDataContext";
import type { View } from "../types";
import { NegotiationPackView } from "./NegotiationPack/NegotiationPackView";
import { PaymentStatusQuery } from "./PaymentStatusQuery";
import ContractReview from "./ContractReview";
import { ReceiptCreation } from "./ReceiptCreation";
import InvoiceAudit from "./InvoiceAudit";
interface AppShellProps {
  initialView: View;
}

const VIEW_ROUTES: Record<View, string> = {
  "dashboard-empty": "/dashboard/empty",
  step1: "/setup/project-details",
  "data-ingestion": "/setup/data-ingestion",
  "projects-empty": "/projects/empty",
  projects: "/projects",
  "spend-intelligence": "/intelligence/spend",
  "supplier-market": "/intelligence/supplier-market",
  "executive-dashboard": "/dashboard/executive",
  "opportunity-insights": "/insights/opportunity",
  "cost-model-analysis": "/intelligence/cost-model-analysis",
  "cost-model-analysis-standalone": "/intelligence/cost-model-analysis/standalone",
  "on-demand-cost-analysis": "/sourcing/on-demand-cost-analysis",
  "cost-model-workflow": "/sourcing/cost-model-workflow",
  "supplier-risk-analysis": "/intelligence/supplier-risk-analysis",
  "po-mismatch-resolution": "/purchase-orders/mismatch-resolution",
  "iac-eligibility-engine": "/purchase-orders/iac-eligibility-engine",
  "pr-creation": "/purchase-orders/pr-creation",
  "negotiation-pack": "/sourcing/negotiation-pack",
  "receipt-creation": "/invoice-payment/receipt-creation",
  "payment-status-query": "/finance/payment-status-query", 
  "contract-review": "/contract-review",
  "invoice-audit": "/invoice-payment/invoice-audit",
};

const getViewFromPath = (pathname: string): View => {
  if (pathname === "/projects") return "projects";
  if (pathname === "/setup/data-ingestion") return "data-ingestion";
  if (pathname.startsWith("/setup")) return "step1";
  if (pathname === "/intelligence/spend") return "spend-intelligence";
  if (pathname === "/intelligence/supplier-market") return "supplier-market";
  if (pathname === "/dashboard/executive") return "executive-dashboard";
  if (pathname === "/insights/opportunity") return "opportunity-insights";
  if (pathname === "/intelligence/cost-model-analysis") return "cost-model-analysis";
  if (pathname === "/intelligence/cost-model-analysis/standalone") return "cost-model-analysis-standalone";
  if (pathname === "/sourcing/on-demand-cost-analysis") return "on-demand-cost-analysis";
  if (pathname === "/sourcing/cost-model-workflow") return "cost-model-workflow";
  if (pathname === "/intelligence/supplier-risk-analysis") return "supplier-risk-analysis";
  if (pathname === "/purchase-orders/mismatch-resolution") return "po-mismatch-resolution";
  if (pathname === "/purchase-orders/iac-eligibility-engine") return "iac-eligibility-engine";
  if (pathname === "/purchase-orders/pr-creation") return "pr-creation";
  if (pathname === "/sourcing/negotiation-pack") return "negotiation-pack";
  if (pathname === "/invoice-payment/receipt-creation") return "receipt-creation";
  if (pathname === "/finance/payment-status-query") return "payment-status-query";
  if (pathname === "/contract-review") return "contract-review";
  if (pathname === "/invoice-payment/invoice-audit") return "invoice-audit";
  return "dashboard-empty";
};

const pushRoute = (view: View) => {
  const nextPath = VIEW_ROUTES[view];
  if (window.location.pathname !== nextPath) {
    window.history.pushState({}, "", nextPath);
  }
};

export function AppShell({ initialView }: AppShellProps) {
  const [view, setView] = useState<View>(() => getViewFromPath(window.location.pathname) ?? initialView);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { registerNavigate } = useExcelData();

  const navigate = (v: View) => {
    setView(v);
    pushRoute(v);
    setSidebarOpen(false);
  };

  // Register navigate in context so deep components can trigger navigation
  useEffect(() => {
    registerNavigate(navigate as (view: string) => void);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setView(getViewFromPath(window.location.pathname));
      setSidebarOpen(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <div className="app-page">
      <Sidebar
        currentView={view}
        onNavigate={navigate}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
      />

      <div className={`app-body${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
        <Topbar onMenuClick={() => setSidebarOpen(true)} />

        <main className="main-content" id="main-content" tabIndex={-1}>
          {view === "dashboard-empty" && <DashboardEmptyView onStart={() => navigate("step1")} />}
          {view === "projects" && <ProjectsListView onStart={() => navigate("step1")} />}
          {view === "projects-empty" && <ProjectsEmptyView onStart={() => navigate("step1")} />}
          {view === "step1" && (
            <SetupWizard
              onLaunch={() => navigate("executive-dashboard")}
              onCancel={() => navigate("projects")}
              startFromDataIngestion={false}
            />
          )}
          {view === "data-ingestion" && (
            <SetupWizard
              onLaunch={() => navigate("executive-dashboard")}
              onCancel={() => navigate("dashboard-empty")}
              startFromDataIngestion
            />
          )}
          {view === "spend-intelligence" && (
            <SpendIntelligenceView onExecutiveDashboard={() => navigate("executive-dashboard")} />
          )}
          {view === "supplier-market" && <SupplierMarketView />}
          {view === "executive-dashboard" && (
            <ExecutiveDashboardView onInsights={() => navigate("opportunity-insights")} />
          )}
          {view === "opportunity-insights" && <OpportunityInsightsView />}
          {view === "cost-model-analysis" && <CostModelAnalysisView />}
          {view === "cost-model-analysis-standalone" && <CostModelAnalysisStandalone />}
          {view === "on-demand-cost-analysis" && <OnDemandCostAnalysisView />}
          {view === "cost-model-workflow" && <CostModelWorkflowView />}
          {view === "supplier-risk-analysis" && <SupplierRiskAnalysisView />}
          {view === "po-mismatch-resolution" && <POMismatchResolutionView />}
          {view === "iac-eligibility-engine" && <IacEligibilityEngineView />}
          {view === "pr-creation" && <PrCreationView />}
          {view === "negotiation-pack" && <NegotiationPackView />}
          {view === "receipt-creation" && <ReceiptCreation />}
          {view === "payment-status-query" && <PaymentStatusQuery />}
          {view === "contract-review" && <ContractReview />}
          {view === "invoice-audit" && <InvoiceAudit />}
        </main>
      </div>
    </div>
  );
}
